import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { adminMainUrl } from "../../../Config";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const EMPTY_LINE = {
    service_type: "",
    HSN_code: "",
    quantity: "",
    rate: "",
    amount: "",
    weight: "",
    weight_unit: "",
    expense_account_id: "",
};

const INITIAL_VALUES = {
    invoice_id: "",
    invoice_number: "",
    invoice_date: "",
    docket_number: "",
    supplier_name: "",
    branch: "",
    invoice_type: "",
    currency: "",
    exchange_rate: "",
    payment_terms: "",
    due_date: "",
    reference_number: "",
    po_number: "",
    invoice_narration: "",

    cgst_rate: "",
    cgst_amount: "",
    sgst_rate: "",
    sgst_amount: "",
    igst_rate: "",
    igst_amount: "",

    taxable_amount: "",
    sub_total: "",
    total_surcharges: "",
    total_tax: "",
    rounding_adjustment: "",
    grand_total: "",
    tds_rate: "",
    tds_amount: "",
    net_payable: "",
    isTDSAllow: false,

    line_data: [{ ...EMPTY_LINE }],
};

// ---------------------------------------------------------------------------
// Validation Schema
// ---------------------------------------------------------------------------
const validationSchema = Yup.object({
    branch: Yup.string().required("Branch is required"),
    invoice_type: Yup.string().required("Invoice Type is required"),
    currency: Yup.string().required("Currency is required"),
    exchange_rate: Yup.string().required("Exchange rate is required"),
    payment_terms: Yup.string().required("Payment terms is required"),
    due_date: Yup.string().required("Due Date is required"),
    docket_number: Yup.string().required("Docket number is required"),
    line_data: Yup.array()
        .of(Yup.object({ amount: Yup.string().required("Line amount required") }))
        .min(1, "Add at least one line item"),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const sanitizeNumber = (value) => {
    const num = Number(value);
    if (value === "") return "";
    if (isNaN(num) || num < 0) return "";
    return num;
};

const mapApiToFormik = (data) => ({
    invoice_id: data.invoice_id || "",
    invoice_number: data.invoice_no || "",
    invoice_date: data.invoice_date?.split("T")[0] || "",
    docket_number: data.docket_no || "",
    supplier_name: String(data.supplier || ""),
    branch: String(data.branch || ""),
    invoice_type: data.invoice_type || "",
    currency: String(data.currency || ""),
    exchange_rate: data.exchange_rate || "",
    payment_terms: data.payment_terms || "",
    due_date: data.due_date?.split("T")[0] || "",
    reference_number: data.reference_no || "",
    po_number: data.po_no || "",
    invoice_narration: data.invoice_narration || "",

    cgst_rate: data.cgst_rate || "",
    cgst_amount: data.cgst_amount || "",
    sgst_rate: data.sgst_rate || "",
    sgst_amount: data.sgst_amount || "",
    igst_rate: data.igst_rate || "",
    igst_amount: data.igst_amount || "",

    taxable_amount: data.taxable_amount || "",
    sub_total: data.sub_total || "",
    total_surcharges: data.total_surcharges || "",
    total_tax: data.total_tax || "",
    rounding_adjustment: data.rounding_adjustment || "",
    grand_total: data.grand_total || "",
    tds_rate: data.tds_rate || "",
    tds_amount: data.tds_amount || "",
    net_payable: data.net_payable || "",
    isTDSAllow: data.tds || false,

    line_data:
        data.line_items?.map((item) => ({
            service_type: item.service_type || "",
            HSN_code: item.HSN_code || "",
            quantity: item.quantity || "",
            rate: item.rate || "",
            amount: item.amount || "",
            weight: item.weight || "",
            weight_unit: item.weight_unit || "",
            expense_account_id: item.expense_account_id || "",
        })) || [{ ...EMPTY_LINE }],
});

const recalcTotals = (values) => {
    const taxableAmount = values.line_data.reduce(
        (sum, row) => sum + Number(row.amount || 0),
        0
    );
    const cgstAmount = (taxableAmount * Number(values.cgst_rate || 0)) / 100;
    const sgstAmount = (taxableAmount * Number(values.sgst_rate || 0)) / 100;
    const igstAmount = (taxableAmount * Number(values.igst_rate || 0)) / 100;
    const totalTax = cgstAmount + sgstAmount + igstAmount;
    const totalSurcharges = Number(values.total_surcharges || 0);
    const roundingAdjustment = Number(values.rounding_adjustment || 0);
    const grandTotal = taxableAmount + totalTax + totalSurcharges + roundingAdjustment;
    const tdsAmount = values.isTDSAllow
        ? (grandTotal * Number(values.tds_rate || 0)) / 100
        : 0;
    return {
        taxable_amount: taxableAmount.toFixed(2),
        cgst_amount: cgstAmount.toFixed(2),
        sgst_amount: sgstAmount.toFixed(2),
        igst_amount: igstAmount.toFixed(2),
        total_tax: totalTax.toFixed(2),
        sub_total: taxableAmount.toFixed(2),
        grand_total: grandTotal.toFixed(2),
        tds_amount: tdsAmount.toFixed(2),
        net_payable: (grandTotal - tdsAmount).toFixed(2),
    };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const EditPurchaseInvoiceModal = ({ show, onHide, editData, onSuccess }) => {
    console.log(editData)
    const token = Cookies.get("token");
    const user_id = Cookies.get("flexichain_user_id");

    const { MasterCurrencyIdAndName } = useSelector(
        (state) => state?.MasterReducer
    );

    const [suppliers, setSuppliers] = useState([]);
    const [branches, setBranches] = useState([]);

    // -------------------------------------------------------------------------
    // Formik setup
    // -------------------------------------------------------------------------
    const formik = useFormik({
        initialValues: INITIAL_VALUES,
        enableReinitialize: false,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async (values, { setSubmitting }) => {
            try {
                const response = await axios.post(
                    `${adminMainUrl}/api/account/standard-purchase-invoice-update`,
                    {
                        id: editData.id,
                        invoice_id: values.invoice_id,
                        invoice_no: values.invoice_number,
                        invoice_date: values.invoice_date,
                        docket_no: values.docket_number,
                        supplier: values.supplier_name,
                        branch: values.branch,
                        invoice_type: values.invoice_type,
                        currency: values.currency,
                        exchange_rate: values.exchange_rate,
                        payment_terms: values.payment_terms,
                        due_date: values.due_date,
                        reference_no: values.reference_number,
                        po_no: values.po_number,
                        invoice_narration: values.invoice_narration,
                        line_items: values.line_data,
                        taxable_amount: values.taxable_amount,
                        cgst_rate: values.cgst_rate,
                        cgst_amount: values.cgst_amount,
                        sgst_rate: values.sgst_rate,
                        sgst_amount: values.sgst_amount,
                        igst_rate: values.igst_rate,
                        igst_amount: values.igst_amount,
                        sub_total: values.sub_total,
                        total_surcharges: values.total_surcharges,
                        total_tax: values.total_tax,
                        rounding_adjustment: values.rounding_adjustment,
                        grand_total: values.grand_total,
                        tds: values.isTDSAllow,
                        tds_rate: values.tds_rate,
                        tds_amount: values.tds_amount,
                        net_payable: values.net_payable,
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "auth-token": token,
                            user_id,
                        },
                    }
                );

                toast.success(response?.data?.msg || "Invoice updated successfully");
                if (onSuccess) onSuccess();
                onHide();
            } catch (err) {
                console.error("Update error:", err);
                toast.error(err?.response?.data?.msg || "Update failed");
            } finally {
                setSubmitting(false);
            }
        },
    });

    const { values, setFieldValue, handleChange, handleSubmit, isSubmitting } = formik;

    // -------------------------------------------------------------------------
    // Load editData into form when modal opens / editData changes
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (show && editData) {
            const mapped = mapApiToFormik(editData);
            formik.setValues(mapped);

            // fetch suppliers list
            axios
                .get(
                    `${adminMainUrl}/api/account/standard-purchase-invoice-organisations`,
                    { headers: { "auth-token": token, user_id } }
                )
                .then((res) => setSuppliers(res?.data?.data || []))
                .catch(console.error);

            // fetch branches for the pre-selected supplier
            if (editData.supplier) {
                fetchBranches(String(editData.supplier), String(editData.branch || ""));
            }
        }
    }, [show, editData]);

    // -------------------------------------------------------------------------
    // Fetch branches helper
    // -------------------------------------------------------------------------
    const fetchBranches = async (supplierId, preSelectBranch = "") => {
        if (!supplierId) {
            setBranches([]);
            return;
        }
        try {
            const res = await axios.post(
                `${adminMainUrl}/api/account/standard-purchase-invoice-organisation-branch`,
                { customer_id: supplierId },
                { headers: { "auth-token": token, user_id } }
            );
            setBranches(res?.data?.data || []);
            if (preSelectBranch) {
                setFieldValue("branch", preSelectBranch, false);
            }
        } catch (err) {
            console.error(err);
            setBranches([]);
        }
    };

    // -------------------------------------------------------------------------
    // Supplier change handler
    // -------------------------------------------------------------------------
    const handleSupplierChange = async (e) => {
        const id = e.target.value;
        setFieldValue("supplier_name", id, false);
        setFieldValue("branch", "", false); // reset branch on supplier change
        await fetchBranches(id);
    };

    // -------------------------------------------------------------------------
    // Number field guard (block negatives)
    // -------------------------------------------------------------------------
    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const num = Number(value);
        setFieldValue(name, num < 0 ? "" : num, false);
    };

    // -------------------------------------------------------------------------
    // Recalculate totals on relevant changes
    // -------------------------------------------------------------------------
    useEffect(() => {
        const totals = recalcTotals(values);
        Object.entries(totals).forEach(([key, val]) =>
            setFieldValue(key, val, false)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        values.line_data,
        values.cgst_rate,
        values.sgst_rate,
        values.igst_rate,
        values.total_surcharges,
        values.rounding_adjustment,
        values.tds_rate,
        values.isTDSAllow,
    ]);

    // -------------------------------------------------------------------------
    // Auto-fill exchange_rate when currency changes
    // -------------------------------------------------------------------------
    useEffect(() => {
        const selected = MasterCurrencyIdAndName?.find(
            (c) => String(c.id) === String(values.currency)
        );
        if (selected) {
            setFieldValue("exchange_rate", selected.exchange_rate || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.currency]);

    // -------------------------------------------------------------------------
    // Line item helpers
    // -------------------------------------------------------------------------
    const handleLineChange = (arrayHelpers, index, field, value, type = "text") => {
        const finalValue = type === "number" ? sanitizeNumber(value) : value;
        const updated = values.line_data.map((row, i) => {
            if (i !== index) return row;
            const updatedRow = { ...row, [field]: finalValue };
            if (field === "quantity" || field === "rate") {
                const qty = Number(field === "quantity" ? finalValue : row.quantity || 0);
                const rate = Number(field === "rate" ? finalValue : row.rate || 0);
                updatedRow.amount = qty * rate;
            }
            return updatedRow;
        });
        setFieldValue("line_data", updated, false);
    };

    const handleAddLine = (arrayHelpers) => {
        const lastRow = values.line_data[values.line_data.length - 1];
        const requiredFields = [
            "service_type",
            "HSN_code",
            "quantity",
            "rate",
            "amount",
            "weight",
            "expense_account_id",
        ];
        const isIncomplete = requiredFields.some(
            (field) => !lastRow[field] || lastRow[field] === ""
        );
        if (isIncomplete) {
            alert("Please fill all fields before adding a new line.");
            return;
        }
        arrayHelpers.push({ ...EMPTY_LINE });
    };

    const handleDeleteLine = (arrayHelpers, index) => {
        if (index === 0) return;
        arrayHelpers.remove(index);
    };

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------
    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Edit Purchase Invoice</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
                <FormikProvider value={formik}>
                    <style>
                        {`
              .padding-pt-9 { padding: 10px; }
              .line-header, .line-row {
                display: flex;
                align-items: center;
                border-bottom: 1px solid #eee;
                padding: 10px 0;
              }
              .col-item { padding: 0 10px; }
              .table-wrapper { min-width: 1200px; }
            `}
                    </style>

                    {/* ---------------------------------------------------------------- */}
                    {/* Header Fields                                                     */}
                    {/* ---------------------------------------------------------------- */}
                    <div className="card p-4">
                        <h5 className="fw-semibold mb-3">Standard Purchase Invoice</h5>
                        <div className="row">

                            {/* invoice_id */}
                            <div className="col-12 col-sm-6 col-md-3 mb-4">
                                <label className="form-label fw-medium">Invoice ID</label>
                                <input
                                    type="text"
                                    name="invoice_id"
                                    className="form-control bg-light"
                                    placeholder="Auto-generated"
                                    disabled
                                    value={values.invoice_id}
                                />
                            </div>

                            {/* invoice_number */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Invoice Number</label>
                                <input
                                    type="text"
                                    name="invoice_number"
                                    className="form-control bg-light"
                                    value={values.invoice_number}
                                    onChange={handleChange}
                                    placeholder="Invoice Number"
                                />
                            </div>

                            {/* invoice_date */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Invoice Date</label>
                                <input
                                    type="date"
                                    name="invoice_date"
                                    className="form-control bg-light"
                                    onChange={handleChange}
                                    value={values.invoice_date}
                                />
                            </div>

                            {/* docket_number */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Docket Number</label>
                                <input
                                    type="text"
                                    name="docket_number"
                                    className="form-control bg-light"
                                    value={values.docket_number}
                                    onChange={handleChange}
                                    placeholder="Docket Number"
                                />
                            </div>

                            {/* supplier_name */}
                            <div className="col-12 col-sm-4 col-md-4 mb-4">
                                <label className="form-label fw-medium">Supplier</label>
                                <select
                                    name="supplier_name"
                                    className="form-select bg-light padding-pt-9"
                                    value={values.supplier_name}
                                    onChange={handleSupplierChange}
                                >
                                    <option value="">Select Supplier Name</option>
                                    {suppliers.map((s) => (
                                        <option value={s.id} key={s.id}>
                                            {`${s.name} (${s.email})`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* branch */}
                            <div className="col-12 col-sm-4 col-md-4 mb-4">
                                <label className="form-label fw-medium">Branch</label>
                                <select
                                    name="branch"
                                    className="form-select bg-light padding-pt-9"
                                    value={values.branch}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Branch</option>
                                    {branches.map((b) => (
                                        <option value={b.id} key={b.id}>
                                            {b.branch_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* invoice_type */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Invoice Type</label>
                                <select
                                    name="invoice_type"
                                    className="form-select bg-light padding-pt-9"
                                    value={values.invoice_type}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Invoice Type</option>
                                    <option value="RCM">RCM</option>
                                    <option value="Regular">Regular</option>
                                </select>
                            </div>

                            {/* currency */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Currency</label>
                                <select
                                    name="currency"
                                    className="form-select bg-light padding-pt-9"
                                    value={values.currency}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Currency Type</option>
                                    {MasterCurrencyIdAndName?.map((item, i) => (
                                        <option key={i} value={item.id}>
                                            {item.currency_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* exchange_rate */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Exchange Rate</label>
                                <input
                                    type="number"
                                    name="exchange_rate"
                                    className="form-control bg-light"
                                    value={values.exchange_rate}
                                    onChange={handleNumberChange}
                                    placeholder="0.00"
                                />
                            </div>

                            {/* payment_terms */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Payment Terms</label>
                                <input
                                    type="text"
                                    name="payment_terms"
                                    className="form-control bg-light"
                                    value={values.payment_terms}
                                    onChange={handleChange}
                                    placeholder="e.g - net 30"
                                />
                            </div>

                            {/* due_date */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Due Date</label>
                                <input
                                    type="date"
                                    name="due_date"
                                    className="form-control bg-light"
                                    onChange={handleChange}
                                    value={values.due_date}
                                />
                            </div>

                            {/* reference_number */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">Reference Number</label>
                                <input
                                    type="text"
                                    name="reference_number"
                                    className="form-control bg-light"
                                    value={values.reference_number}
                                    onChange={handleChange}
                                    placeholder="Reference Number"
                                />
                            </div>

                            {/* po_number */}
                            <div className="col-12 col-sm-4 col-md-3 mb-4">
                                <label className="form-label fw-medium">PO Number</label>
                                <input
                                    type="text"
                                    name="po_number"
                                    className="form-control bg-light"
                                    value={values.po_number}
                                    onChange={handleChange}
                                    placeholder="PO Number"
                                />
                            </div>

                            {/* invoice_narration */}
                            <div className="col-12 mb-4">
                                <label className="form-label fw-medium">Invoice Narration</label>
                                <input
                                    type="text"
                                    name="invoice_narration"
                                    className="form-control bg-light"
                                    value={values.invoice_narration}
                                    onChange={handleChange}
                                    placeholder="Invoice Narration"
                                />
                            </div>

                        </div>
                    </div>

                    {/* ---------------------------------------------------------------- */}
                    {/* Line Items (FieldArray)                                          */}
                    {/* ---------------------------------------------------------------- */}
                    <div className="card p-4 mt-4 w-100">
                        <FieldArray name="line_data">
                            {(arrayHelpers) => (
                                <>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="fw-semibold mb-3">Line Items</h5>
                                        <button
                                            type="button"
                                            className="btn btn-primary px-4"
                                            onClick={() => handleAddLine(arrayHelpers)}
                                        >
                                            + Add Line
                                        </button>
                                    </div>

                                    <div className="table-responsive" style={{ overflowX: "auto" }}>
                                        <div className="table-wrapper">
                                            {/* HEADER */}
                                            <div className="line-header fw-semibold">
                                                <div className="col-item" style={{ width: "40px" }}>#</div>
                                                <div className="col-item" style={{ width: "200px" }}>Service Type</div>
                                                <div className="col-item" style={{ width: "120px" }}>HSN Code</div>
                                                <div className="col-item" style={{ width: "80px" }}>Qty</div>
                                                <div className="col-item" style={{ width: "120px" }}>Rate</div>
                                                <div className="col-item" style={{ width: "150px" }}>Amount</div>
                                                <div className="col-item" style={{ width: "100px" }}>Weight</div>
                                                <div className="col-item" style={{ width: "80px" }}>Unit</div>
                                                <div className="col-item" style={{ width: "200px" }}>Expense A/c</div>
                                                <div className="col-item" style={{ width: "60px" }}>X</div>
                                            </div>

                                            {/* ROWS */}
                                            {values.line_data.map((row, index) => (
                                                <div className="line-row" key={index}>
                                                    {/* Index */}
                                                    <div className="col-item" style={{ width: "40px" }}>
                                                        {index + 1}
                                                    </div>

                                                    {/* Service Type */}
                                                    <div className="col-item" style={{ width: "200px" }}>
                                                        <select
                                                            className="form-select"
                                                            value={row.service_type}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "service_type", e.target.value)
                                                            }
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="Freight">Air Freight Charges</option>
                                                            <option value="Fuel Surcharge">Fuel Surcharge</option>
                                                        </select>
                                                    </div>

                                                    {/* HSN Code */}
                                                    <div className="col-item" style={{ width: "120px" }}>
                                                        <input
                                                            className="form-control"
                                                            value={row.HSN_code}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "HSN_code", e.target.value)
                                                            }
                                                        />
                                                    </div>

                                                    {/* Quantity */}
                                                    <div className="col-item" style={{ width: "80px" }}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.quantity}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "quantity", e.target.value, "number")
                                                            }
                                                        />
                                                    </div>

                                                    {/* Rate */}
                                                    <div className="col-item" style={{ width: "120px" }}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.rate}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "rate", e.target.value, "number")
                                                            }
                                                        />
                                                    </div>

                                                    {/* Amount (auto-calculated) */}
                                                    <div className="col-item" style={{ width: "150px" }}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.amount}
                                                            disabled
                                                            readOnly
                                                        />
                                                    </div>

                                                    {/* Weight */}
                                                    <div className="col-item" style={{ width: "100px" }}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.weight}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "weight", e.target.value, "number")
                                                            }
                                                        />
                                                    </div>

                                                    {/* Weight Unit */}
                                                    <div className="col-item" style={{ width: "80px" }}>
                                                        <select
                                                            className="form-select"
                                                            value={row.weight_unit}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "weight_unit", e.target.value)
                                                            }
                                                        >
                                                            <option value="">Select Unit</option>
                                                            <option value="KG">KG</option>
                                                        </select>
                                                    </div>

                                                    {/* Expense A/c */}
                                                    <div className="col-item" style={{ width: "200px" }}>
                                                        <select
                                                            className="form-select"
                                                            value={row.expense_account_id}
                                                            onChange={(e) =>
                                                                handleLineChange(arrayHelpers, index, "expense_account_id", e.target.value)
                                                            }
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="1">Expense 1</option>
                                                            <option value="2">Expense 2</option>
                                                        </select>
                                                    </div>

                                                    {/* Delete */}
                                                    <div className="col-item" style={{ width: "60px" }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleDeleteLine(arrayHelpers, index)}
                                                        >
                                                            X
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </FieldArray>
                    </div>

                    {/* ---------------------------------------------------------------- */}
                    {/* Tax Calculator + Invoice Summary                                  */}
                    {/* ---------------------------------------------------------------- */}
                    <div className="row mt-4">
                        {/* Tax Calculator */}
                        <div className="card p-4 col-12 col-md-6">
                            <h5 className="fw-semibold mb-3">Tax Calculator</h5>
                            <div className="row">
                                <div className="col-6 mb-2">
                                    <h6>Taxable Amount</h6>
                                    <div className="px-3 py-2 bg-light rounded-3 mt-1 fw-semibold">
                                        {values.taxable_amount || 0}
                                    </div>
                                </div>

                                <hr />

                                {/* CGST */}
                                <div className="col-6 mb-4">
                                    <h6>CGST Rate (%)</h6>
                                    <input
                                        type="number"
                                        name="cgst_rate"
                                        className="form-control bg-light"
                                        value={values.cgst_rate}
                                        onChange={handleNumberChange}
                                        placeholder="CGST Rate"
                                    />
                                </div>
                                <div className="col-6 mb-4">
                                    <h6>CGST Amount</h6>
                                    <div className="px-3 py-2 bg-light rounded-3 mt-1 fw-semibold">
                                        {values.cgst_amount}
                                    </div>
                                </div>

                                {/* SGST */}
                                <div className="col-6 mb-4">
                                    <h6>SGST Rate (%)</h6>
                                    <input
                                        type="number"
                                        name="sgst_rate"
                                        className="form-control bg-light"
                                        value={values.sgst_rate}
                                        onChange={handleNumberChange}
                                        placeholder="SGST Rate"
                                    />
                                </div>
                                <div className="col-6 mb-4">
                                    <h6>SGST Amount</h6>
                                    <div className="px-3 py-2 bg-light rounded-3 mt-1 fw-semibold">
                                        {values.sgst_amount}
                                    </div>
                                </div>

                                {/* IGST */}
                                <div className="col-6 mb-4">
                                    <h6>IGST Rate (%)</h6>
                                    <input
                                        type="number"
                                        name="igst_rate"
                                        className="form-control bg-light"
                                        value={values.igst_rate}
                                        onChange={handleNumberChange}
                                        placeholder="IGST Rate"
                                    />
                                </div>
                                <div className="col-6 mb-4">
                                    <h6>IGST Amount</h6>
                                    <div className="px-3 py-2 bg-light rounded-3 mt-1 fw-semibold">
                                        {values.igst_amount}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Summary */}
                        <div className="card p-4 col-12 col-md-6">
                            <h5 className="fw-semibold mb-3">Invoice Summary</h5>

                            <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">Sub Total</span>
                                <span>₹{values.taxable_amount || 0}</span>
                            </div>

                            <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">Total Surcharges</span>
                                <span>₹ {values.total_surcharges || 0}</span>
                            </div>

                            <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">Total Tax</span>
                                <span>₹ {values.total_tax || 0}</span>
                            </div>

                            <div className="d-flex justify-content-between py-2 border-bottom">
                                <span className="text-muted">Rounding Adjustment</span>
                                <span>₹ {values.rounding_adjustment || 0}</span>
                            </div>

                            <div className="d-flex justify-content-between py-2 border-bottom fw-semibold">
                                <span>Grand Total</span>
                                <span>₹ {values.grand_total || 0}</span>
                            </div>

                            {/* TDS */}
                            <div className="mt-3">
                                <input
                                    type="checkbox"
                                    name="isTDSAllow"
                                    checked={values.isTDSAllow}
                                    onChange={handleChange}
                                />
                                <label className="fw-medium mb-1 ms-2">TDS Applicable</label>
                                {values.isTDSAllow && (
                                    <div className="row g-2 mt-1">
                                        <div className="col-6">
                                            <input
                                                type="number"
                                                name="tds_rate"
                                                className="form-control bg-light"
                                                placeholder="TDS Rate (%)"
                                                onChange={handleNumberChange}
                                                value={values.tds_rate}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <input
                                                type="text"
                                                className="form-control bg-light"
                                                placeholder="TDS Amount"
                                                disabled
                                                value={values.tds_amount}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Net Payable */}
                            <div className="d-flex justify-content-between py-3 fw-semibold">
                                <span>Net Payable</span>
                                <span>₹ {values.net_payable || "0.00"}</span>
                            </div>
                        </div>
                    </div>

                </FormikProvider>
            </Modal.Body>

            {/* ------------------------------------------------------------------ */}
            {/* Footer Buttons                                                       */}
            {/* ------------------------------------------------------------------ */}
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                    <i className="fas fa-xmark me-1"></i> Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    <i className="fas fa-save me-1"></i>{" "}
                    {isSubmitting ? "Updating..." : "Update"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditPurchaseInvoiceModal;